import { Tooltip } from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { motion, useReducedMotion } from 'framer-motion';

import type { TStatusIndicatorState } from './statusIndicator.types';

const DOT_CLASSES: Record<Exclude<TStatusIndicatorState, 'running'>, string> = {
  idle: 'size-[5.5px] border-[0.5px] border-border-primary-hover',
  unread: 'size-[6px] bg-fg-brand',
  failed: 'size-[6px] bg-fg-error',
};

const STATE_LABELS: Record<TStatusIndicatorState, string | null> = {
  idle: null,
  running: 'Running',
  unread: 'Unread',
  failed: 'Failed',
};

const TOOLTIP_LABELS: Record<TStatusIndicatorState, string> = {
  idle: 'No new activity',
  running: 'Running',
  unread: 'Unread',
  failed: 'Failed',
};

const SPINNER_ARC =
  'M5 0C5.98891 -4.32265e-08 6.95561 0.293245 7.77785 0.842652C8.6001 1.39206 9.24096 2.17295 9.6194 3.08658C9.99784 4.00021 10.0969 5.00555 9.90393 5.97545C9.711 6.94536 9.2348 7.83627 8.53553 8.53553C7.83627 9.2348 6.94536 9.711 5.97545 9.90393C5.00555 10.0969 4.00021 9.99784 3.08658 9.6194C2.17295 9.24096 1.39206 8.6001 0.842652 7.77785C0.293245 6.95561 -8.64529e-08 5.98891 0 5L1.9 5C1.9 5.61312 2.08181 6.21248 2.42244 6.72227C2.76308 7.23206 3.24723 7.62939 3.81368 7.86403C4.38013 8.09866 5.00344 8.16005 5.60478 8.04043C6.20612 7.92082 6.75849 7.62557 7.19203 7.19203C7.62557 6.75849 7.92082 6.20612 8.04043 5.60478C8.16005 5.00344 8.09866 4.38013 7.86403 3.81368C7.62939 3.24723 7.23206 2.76308 6.72227 2.42244C6.21248 2.08181 5.61312 1.9 5 1.9L5 0Z';

const SPINNER_DURATION_SECONDS = 2.25;

export function StatusIndicator({ state }: { state: TStatusIndicatorState }) {
  const reduceMotion = useReducedMotion() ?? false;
  const label = STATE_LABELS[state];

  return (
    <Tooltip content={TOOLTIP_LABELS[state]} side="top" sideOffset={6} asChild>
      <span
        className="flex size-4 shrink-0 items-center justify-center"
        role={label ? 'img' : undefined}
        aria-label={label ?? undefined}
        aria-hidden={label ? undefined : true}
      >
        {state === 'running' ? (
          <motion.svg
            className="size-[10px]"
            viewBox="0 0 10 10"
            fill="none"
            animate={reduceMotion ? undefined : { rotate: 360 }}
            transition={
              reduceMotion
                ? undefined
                : {
                    duration: SPINNER_DURATION_SECONDS,
                    ease: 'linear',
                    repeat: Infinity,
                  }
            }
          >
            <path d={SPINNER_ARC} className="fill-fg-brand" />
          </motion.svg>
        ) : (
          <span className={classNames('rounded-full', DOT_CLASSES[state])} />
        )}
      </span>
    </Tooltip>
  );
}
